"use client";

import * as React from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CanvasCourse } from "@/lib/types";
import { Notebook } from "../../../client";
import { StudySetButton } from "./study-set-button";
import { NotebookExtended } from "../../../types";

interface StudySetGridProps {
  title: string;

  notebooks: NotebookExtended[];
  courses: CanvasCourse[];

  onOpenNotebook: (notebook: Notebook) => void;

  showCreate?: boolean;
  onCreate?: () => void;
  onDelete?: (notebook: Notebook) => void;
}

export function StudySetGrid({
  title,
  notebooks,
  courses,
  onOpenNotebook,
  showCreate = false,
  onCreate,
  onDelete,
}: StudySetGridProps) {
  const [search, setSearch] = React.useState("");
  const [courseFilter, setCourseFilter] = React.useState<string | "all">("all");

  const filteredNotebooks = notebooks
    .filter((notebook) => {
      const matchesSearch = notebook.name
        .toLowerCase()
        .includes(search.toLowerCase());

      const matchesCourse =
        courseFilter === "all" || notebook.course_id === courseFilter;

      return matchesSearch && matchesCourse;
    })
    .sort(
      (a, b) =>
        new Date(b.created_at).valueOf() - new Date(a.created_at).valueOf()
    );

  return (
    <section className="mt-12 rounded-xl pt-10">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold text-theme-0">{title}</h2>

        <div className="flex items-center gap-2">
          <Input
            placeholder="Search study sets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-56"
            variant="search"
          />

          <Select value={courseFilter} onValueChange={setCourseFilter}>
            <SelectTrigger className="h-9 w-44">
              <SelectValue placeholder="All courses" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              {courses.map((course) => (
                <SelectItem key={course.id} value={course.id.toString()}>
                  {course.code || course.default}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <StudySetButton variant="create" onClick={onCreate} />

        {filteredNotebooks.map((set) => {
          const course = courses.find((c) => c.id.toString() === set.course_id);

          return (
            <StudySetButton
              key={set.id}
              variant="existing"
              notebook={set}
              course={course}
              onClick={() => onOpenNotebook(set)}
              onDelete={onDelete}
            />
          );
        })}
      </div>
    </section>
  );
}
